import cx from 'classnames'
import Styles from './Form.module.scss'
import { ActionsProps, FieldsProps, FormProps, SectionContentProps, SectionProps } from './Types'
import { forwardRef, PropsWithChildren, useRef } from 'react'
import { Heading } from 'almanac-ui/Heading/Heading'
import { useFormikContext } from 'formik'
import { ArrowRight } from 'almanac-ui/icons'
import { Button } from 'blaze-ui/Button'
import { useSections } from 'components/Wizards/Wizard/Form/Sections/SectionsProvider'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { useIntersection } from 'react-use'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ArrowLeft } from 'blaze-ui/icons'
import { Icon } from 'blaze-ui/icons/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const Section = ({ children, isDisabled = false, onClick, backgroundColor }: PropsWithChildren<SectionProps>) => (
  <div onClick={onClick} className={cx(Styles.section, { [Styles.disabled]: isDisabled })} style={{ backgroundColor }}>
    {children}
  </div>
)

const SectionContent = ({ children, className }: PropsWithChildren<SectionContentProps>) => (
  <div className={cx(Styles.content, className)}>{children}</div>
)

const Fields = ({ children, className }: FieldsProps) => <div className={cx(Styles.fields, className)}>{children}</div>

const Actions = ({ children, isSticky = false }: ActionsProps) => {
  const { isAutoFillSectionsEnabled } = useSections()
  const ref = useRef<HTMLDivElement | null>(null)
  const observer = useIntersection(ref, {})
  const isCompact = isSticky && Boolean(observer && !observer.isIntersecting)

  return (
    <>
      <div className={Styles.actionsIntersection} ref={ref} />
      <div
        className={cx(Styles.actions, {
          [Styles.disabled]: !isAutoFillSectionsEnabled,
          [Styles.sticky]: isAutoFillSectionsEnabled && isSticky,
          [Styles.compact]: isCompact,
        })}
      >
        {children}
      </div>
    </>
  )
}

const PrimaryActions = forwardRef<HTMLDivElement, ActionsProps>(({ children }, ref) => (
  <div ref={ref} className={Styles.primaryActionsGroup}>
    {children}
  </div>
))

const Root = ({ title, children }: FormProps) => {
  return (
    <div className={Styles.form}>
      {title && (
        <div className={Styles.titleWrapper}>
          <Heading level={2} id="wizard-header">
            <span>{title}</span>
          </Heading>
        </div>
      )}
      {children}
    </div>
  )
}

const SubmitButton = ({
  children,
  variant = 'primary',
  endIcon = ArrowRight,
  isDisabled,
  onPress,
}: PropsWithChildren<Pick<ButtonProps, 'variant' | 'endIcon' | 'isDisabled' | 'onPress'>>) => {
  const { submitForm, isSubmitting } = useFormikContext()

  return (
    <Button
      size="lg"
      variant={variant}
      onPress={(event) => {
        onPress?.(event)
        submitForm()
      }}
      endIcon={endIcon}
      isDisabled={isDisabled || isSubmitting}
      fullWidth={window.innerWidth < 450}
    >
      {children}
    </Button>
  )
}

const PreviousButton = ({
  children,
  variant = 'tertiary',
  frontIcon = ArrowLeft,
  isDisabled,
  size = 'lg',
  onPress,
}: PropsWithChildren<
  Pick<ButtonProps, 'variant' | 'isDisabled' | 'onPress' | 'size'> & {
    frontIcon?: Icon | null
  }
>) => {
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const { isMobile } = useResponsiveDesign()

  return (
    <Button
      size={size}
      variant={isDesktopWizardsUpdatesOn ? 'editor' : variant}
      onPress={onPress}
      frontIcon={frontIcon || undefined}
      isDisabled={isDisabled}
      fullWidth={isMobile}
    >
      {children}
    </Button>
  )
}

export const Form = {
  Root,
  Fields,
  Actions,
  PrimaryActions,
  SubmitButton,
  PreviousButton,
  Section,
  SectionContent,
}
